// Setup › Texts and email on the SettingsPage pattern (BL-189): the words customers get (templates.list),
// five and then Show all, each opening a FormSheet to change its words, with the fields it can fill in
// (ActionGroup's Put in); then what goes out on its own, and quiet hours and consent (RULE_SECTIONS).
// As Setup.tsx's TemplatesTab draws them in Blueprint, with nothing changed: a body may only use the
// fields its template offers, so Save is off until it does; someone who can't change them reads them.
// Riverstone keeps TemplatesTab.
import { useCallback, useEffect, useState } from 'react';
import { ActionGroup, KeyValue, Link, TextField } from '@flowology/ui-web/fl';
import { FormSheet, SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { BP_LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { settingsRights } from './settings-model.ts';
import { setupRights, templateInput, unknownFields, type Template } from './setup-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

const FIVE = 5;
const READ_ONLY = 'Only the owner or a manager changes these.';
const purposeOf = (t: Template) => {
  const w = t.purpose.replace(/_/g, ' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
};
const channelWord = (t: Template) => (t.channel === 'sms' ? 'Text' : 'Email');

export function TemplatesPage({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  const rights = settingsRights(held);
  const setup = setupRights(held);
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('templates').label ?? 'Texts and email',
            lede: BP_LEDE.templates,
          }}
        >
          <SetupRuleSections page="templates" top rights={rights} />
          <TheWords canEdit={setup.templates} />
          <SetupRuleSections page="templates" rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

function TheWords({ canEdit }: { canEdit: boolean }) {
  const [rows, setRows] = useState<Template[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<Template | null>(null);
  const load = useCallback(() => {
    client
      .call<{ templates: Template[] }>('templates.list', {})
      .then((r) => {
        setRows(r.templates);
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  }, []);
  useEffect(load, [load]);
  const shown = rows && !all ? rows.slice(0, FIVE) : rows;
  const more = rows && !all && rows.length > FIVE;
  return (
    <SettingsSection
      title="The words"
      line="The words customers get in texts and emails."
      {...(canEdit ? {} : { note: READ_ONLY })}
      status={rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
      emptyText="No texts or emails yet."
      {...(error ? { failedText: error } : {})}
      onRetry={load}
      {...(more ? { foot: <Link onPress={() => setAll(true)}>Show all {rows.length}</Link> } : {})}
    >
      {shown?.map((t) => (
        <SettingRow
          key={t.id}
          name={purposeOf(t)}
          tag={channelWord(t)}
          help={`${t.subject ? `${t.subject}: ` : ''}${t.body}`}
          control={
            <Link label={`${canEdit ? 'Edit' : 'Open'} ${purposeOf(t)}`} onPress={() => setOpen(t)}>
              {canEdit ? 'Edit' : 'Open'}
            </Link>
          }
        />
      ))}
      {open && (
        <TemplateSheet
          key={open.id}
          t={open}
          canEdit={canEdit}
          onClose={() => setOpen(null)}
          onSaved={load}
        />
      )}
    </SettingsSection>
  );
}

function TemplateSheet({
  t,
  canEdit,
  onClose,
  onSaved,
}: {
  t: Template;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [subject, setSubject] = useState(t.subject ?? '');
  const [body, setBody] = useState(t.body);
  const save = useSaveKey(`${subject}|${body}`);
  const unknown = unknownFields(t, `${subject} ${body}`);
  const insert = (f: string) => setBody(`${body}${body.endsWith(' ') || !body ? '' : ' '}{{${f}}}`);
  const title = `${purposeOf(t)} (${channelWord(t).toLowerCase()})`;
  if (!canEdit)
    return (
      <FormSheet
        isOpen
        title={title}
        onClose={onClose}
        isDirty={false}
        onSubmit={onClose}
        note={READ_ONLY}
        isReadOnly
      >
        <KeyValue
          isStackedOnPhone
          items={[
            ...(t.channel === 'email' ? [{ label: 'Subject', value: t.subject || '—' }] : []),
            { label: 'Message', value: t.body },
          ]}
        />
      </FormSheet>
    );
  return (
    <FormSheet
      isOpen
      title={title}
      onClose={onClose}
      submitLabel="Save"
      isDirty={body !== t.body || subject !== (t.subject ?? '')}
      isSubmitDisabled={unknown.length > 0}
      {...(unknown.length > 0
        ? {
            note: `This message can’t fill in ${unknown.map((f) => `{{${f}}}`).join(', ')}. Use one of the fields above.`,
          }
        : {})}
      onSubmit={async () => {
        const built = templateInput(t, subject, body);
        if ('error' in built) throw new Error(built.error);
        try {
          await client.call('templates.update', built.input, { idempotencyKey: save.key });
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        save.renew();
        onSaved();
      }}
    >
      {t.channel === 'email' && <TextField label="Subject" value={subject} onChange={setSubject} />}
      <TextField
        label="Message"
        multiline
        value={body}
        onChange={setBody}
        {...(t.channel === 'sms'
          ? { description: `${body.length} characters. Texts over 160 go as more than one message.` }
          : {})}
      />
      {t.fields.length > 0 && (
        <ActionGroup
          label="Put in"
          items={t.fields.map((f) => ({ id: f, label: `{{${f}}}` }))}
          onAction={insert}
        />
      )}
    </FormSheet>
  );
}
